import { useIntl } from 'react-intl'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { useStorefrontAccessDecision } from '@b-stock/fe-core/bstock-next/queries'
import { SpinnerDots } from '@b-stock/fe-core/bstock-react'

import { ErrorBannerComponent } from '@/buyer-portal/components/ErrorBanner'
import { FullPageStateCard } from '@/buyer-portal/components/layouts/CardLayout/card/ui'
import useAccountId from '@/buyer-portal/helpers/useAccountId'
import { StorefrontAccessCheckFailedCard } from '@/components/storefrontAccess/StorefrontAccessCheckFailed'
import StorefrontAccessGate from '@/components/storefrontAccess/StorefrontAccessGate'
import { useAttAccessCheckAccountId } from '@/components/storefrontAccess/useAttAccessRoute'
import { TermsLoadError } from '@/components/termsRestrictions/TermsLoadError'
import { getTermsPoints } from '@/components/termsRestrictions/termsPoints/getTermsPoints'
import {
  TERMS_POINTS_WIDTH_REM,
  TermsRestrictions,
} from '@/components/termsRestrictions/TermsRestrictions'
import { useCmsSellerTerms } from '@/components/termsRestrictions/useCmsSellerTerms'
import { FeatureFlags } from '@/helpers/featureFlags'
import { PermissionGranule, usePermission } from '@/helpers/permissions'
import { type ListingDetails } from '@/queries/listingDetailsQuery'

// The gate's own card width, for the LDP redesign's full-page card.
const GATE_CARD_WIDTH = '38.75rem'

type GuardProps = {
  listing: ListingDetails
  children: React.ReactNode
}

/**
 * Reads account's AT&T access decision and shows the access gate instead of the listing when
 * it is enforced and denies access. Loading shows the page's loading state; a failed read shows
 * a retry state and never the listing, so a failure never grants access.
 */
const AttListingAccessCheck = ({
  accountId,
  children,
}: {
  accountId: string
  children: React.ReactNode
}) => {
  const decision = useStorefrontAccessDecision(accountId)
  const enableLdpRedesign = useFlag(FeatureFlags.ENABLE_LDP_REDESIGN)

  const loading = enableLdpRedesign ? (
    <FullPageStateCard>
      <SpinnerDots />
    </FullPageStateCard>
  ) : (
    <SpinnerDots />
  )

  if (decision.isError) {
    if (decision.isFetching) return loading
    const retry = () => void decision.refetch()
    return enableLdpRedesign ? (
      <FullPageStateCard>
        <StorefrontAccessCheckFailedCard onRetry={retry} framed={false} />
      </FullPageStateCard>
    ) : (
      <StorefrontAccessCheckFailedCard onRetry={retry} />
    )
  }
  if (decision.isPending) return loading

  if (decision.data.enforced && !decision.data.hasAccess) {
    return enableLdpRedesign ? (
      <FullPageStateCard $width={GATE_CARD_WIDTH}>
        <StorefrontAccessGate framed={false} />
      </FullPageStateCard>
    ) : (
      <StorefrontAccessGate />
    )
  }

  return children
}

/**
 * Who may see a listing: sign-in and permissions first, then (AT&T listings only) the access
 * decision, then the terms step.
 */
export const ListingTermsAcceptanceGuard = ({
  listing,
  children,
}: GuardProps): React.ReactNode => {
  const attAccessAccountId = useAttAccessCheckAccountId(listing)
  const termsGuard = (
    <ListingTermsGuard listing={listing}>{children}</ListingTermsGuard>
  )

  if (!attAccessAccountId) return termsGuard
  return (
    <AttListingAccessCheck accountId={attAccessAccountId}>
      {termsGuard}
    </AttListingAccessCheck>
  )
}

const ListingTermsGuard = ({ listing, children }: GuardProps) => {
  const accountId = useAccountId()
  const isLoggedIn = !!accountId

  const canViewWithoutAcceptingTerms = usePermission(
    PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS
  )
  const canAcceptTerms = usePermission(
    PermissionGranule.FEBP_LISTING_TERMS_ACCEPT
  )

  // Only check terms if user is logged in, and they don't have bypass permissions
  const termsPolicy = listing.termsRestrictions?.[0]
  const shouldCheckTerms =
    isLoggedIn && !canViewWithoutAcceptingTerms && !!termsPolicy

  const termsDocUrl = listing.termsMetadata?.docUrl

  // The listing already carries the buyer's accepted policies, so acceptance is known here
  // and the CMS is asked only when the terms page can actually appear. The same check runs
  // again inside the loading manager below, where the access state is decided.
  const acceptedPolicies = listing.acceptedPolicies
  const hasAlreadyAcceptedTerms =
    !!acceptedPolicies &&
    (listing.termsRestrictions?.some(
      (term: string) => term in acceptedPolicies
    ) ??
      false)

  // The CMS accept-terms body, fetched only when the buyer may need the page. Settles to
  // null on any failure, so the hardcoded copy still renders and acceptance is never blocked.
  const cmsSellerTerms = useCmsSellerTerms({
    sellerSlug: listing.seller.storefront?.slug,
    storeFrontId: listing.storefrontId,
    enabled: shouldCheckTerms && !hasAlreadyAcceptedTerms,
  })

  const enableLdpRedesign = useFlag(FeatureFlags.ENABLE_LDP_REDESIGN)

  const { formatMessage } = useIntl()

  // Block the page (no listing content) when terms are required but
  // the doc URL is missing, and log so the bad-data condition is
  // visible in Datadog.
  if (shouldCheckTerms && !termsDocUrl) {
    const termsLoadError = (
      <TermsLoadError
        listingId={listing._id}
        termsMetadata={listing.termsMetadata}
      />
    )
    return (
      <>
        {enableLdpRedesign ? (
          <FullPageStateCard>{termsLoadError}</FullPageStateCard>
        ) : (
          termsLoadError
        )}
      </>
    )
  }

  const accessState = (():
    | 'allowed'
    | 'needsTermsAcceptance'
    | 'unauthorized' => {
    /*
     * Note - This is a temporary solution and does not follow our permission
     * inspector patterns. We'll need to revisit this after the Costco release to find
     * a more elegant solution that adheres to our standards
     */
    // Warehouse managers can see own listings without terms restrictions
    if (accountId === listing.accountId) return 'allowed'

    // Unauthorized users (non-Costco sellers, etc.)
    if (isLoggedIn && !canViewWithoutAcceptingTerms && !canAcceptTerms) {
      return 'unauthorized'
    }

    // Privileged users (service/sellers) can always view
    if (!isLoggedIn || canViewWithoutAcceptingTerms) return 'allowed'

    // Buyers with terms restrictions
    if (canAcceptTerms && shouldCheckTerms) {
      return hasAlreadyAcceptedTerms ? 'allowed' : 'needsTermsAcceptance'
    }

    // Buyers without terms restrictions
    if (canAcceptTerms) return 'allowed'

    // Fallback to unauthorized
    return 'unauthorized'
  })()

  const termsDisplayName = listing.termsMetadata?.displayName

  if (accessState === 'unauthorized') {
    return (
      <ErrorBannerComponent
        errorMessage={formatMessage({
          id: 'Listing.errors.notAuthorized',
        })}
      />
    )
  }

  if (accessState === 'needsTermsAcceptance') {
    if (!listing.seller.storefront || !listing.termsRestrictions?.[0]) {
      return (
        <ErrorBannerComponent
          errorMessage={formatMessage({
            id: 'Listing.errors.missingTerms',
          })}
        />
      )
    }
    // Wait for the CMS answer so the page never flashes the hardcoded copy
    // before CMS copy replaces it.
    if (!cmsSellerTerms.isSettled) {
      return enableLdpRedesign ? (
        <FullPageStateCard>
          <SpinnerDots />
        </FullPageStateCard>
      ) : (
        <SpinnerDots />
      )
    }
    const termsPoints = getTermsPoints({
      sellerSlug: listing.seller.storefront.slug,
      termsDisplayName,
      termsDocUrl,
      cmsTerms: cmsSellerTerms.data,
    })
    const termsRestrictions = (
      <TermsRestrictions
        listingId={listing._id}
        policy={listing.termsRestrictions[0]}
        storeFrontName={listing.seller.storefront.name}
        sellerDisplayName={listing.seller.account.displayName}
        termsDisplayName={termsDisplayName}
        termsDocUrl={termsDocUrl}
        termsPoints={termsPoints}
      />
    )
    return enableLdpRedesign ? (
      <FullPageStateCard
        $width={termsPoints ? `${TERMS_POINTS_WIDTH_REM + 4}rem` : undefined}
      >
        {termsRestrictions}
      </FullPageStateCard>
    ) : (
      termsRestrictions
    )
  }

  // accessState === 'allowed'
  return children
}
